import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const [launchDate, setLaunchDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bank, setBank] = useState({ bank_name: "", bank_account_name: "", bank_bsb: "", bank_account_number: "" });
  const [savingBank, setSavingBank] = useState(false);
  const [biz, setBiz] = useState({ abn: "", business_address: "", business_phone: "", business_email: "" });
  const [savingBiz, setSavingBiz] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => {
        setLaunchDate(toLocalInput(data.launch_date));
        setBank({
          bank_name: data.bank_name || "",
          bank_account_name: data.bank_account_name || "",
          bank_bsb: data.bank_bsb || "",
          bank_account_number: data.bank_account_number || "",
        });
        setBiz({
          abn: data.abn || "",
          business_address: data.business_address || "",
          business_phone: data.business_phone || "",
          business_email: data.business_email || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!launchDate) return;
    setSaving(true);
    try {
      await api.put("/admin/settings", { launch_date: new Date(launchDate).toISOString(), ...bank });
      toast.success("Launch date updated — the homepage countdown now uses it");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      await api.put("/admin/settings", bank);
      toast.success("Bank details saved — customers now see them at checkout");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingBank(false);
    }
  };

  const saveBiz = async (e) => {
    e.preventDefault();
    setSavingBiz(true);
    try {
      await api.put("/admin/settings", { ...bank, ...biz });
      toast.success("Business details saved — they now appear on invoices");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingBiz(false);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-serif text-3xl md:text-4xl font-light mb-2">Settings</h1>
      <p className="text-sm text-[#4A4A4A] mb-10">Control what your visitors see.</p>

      <form onSubmit={save} className="bg-white border border-[#C5A059]/25 p-8 max-w-lg space-y-6" data-testid="settings-form">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-1">Countdown</p>
          <h2 className="font-serif text-2xl font-light">Launch Date</h2>
          <p className="text-sm text-[#4A4A4A] mt-2">The coming-soon countdown on your homepage counts down to this moment.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="launch-date" className="text-xs tracking-widest uppercase">Date & Time</Label>
          <Input id="launch-date" type="datetime-local" required
            value={launchDate} onChange={(e) => setLaunchDate(e.target.value)}
            disabled={loading} data-testid="launch-date-input" className="rounded-none" />
        </div>
        <button type="submit" disabled={saving || loading} data-testid="settings-save-button"
          className="bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <form onSubmit={saveBank} className="bg-white border border-[#C5A059]/25 p-8 max-w-lg space-y-5 mt-8" data-testid="bank-settings-form">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-1">Checkout</p>
          <h2 className="font-serif text-2xl font-light">Bank Transfer Details</h2>
          <p className="text-sm text-[#4A4A4A] mt-2">Shown to customers who choose bank transfer at checkout, and emailed with their order.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase">Bank Name</Label>
          <Input value={bank.bank_name} onChange={(e) => setBank((b) => ({ ...b, bank_name: e.target.value }))} data-testid="bank-name-input" className="rounded-none" placeholder="e.g. Commonwealth Bank" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase">Account Name</Label>
          <Input value={bank.bank_account_name} onChange={(e) => setBank((b) => ({ ...b, bank_account_name: e.target.value }))} data-testid="bank-account-name-input" className="rounded-none" placeholder="Australian Luxe Pty Ltd" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase">BSB</Label>
            <Input value={bank.bank_bsb} onChange={(e) => setBank((b) => ({ ...b, bank_bsb: e.target.value }))} data-testid="bank-bsb-input" className="rounded-none" placeholder="000-000" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase">Account Number</Label>
            <Input value={bank.bank_account_number} onChange={(e) => setBank((b) => ({ ...b, bank_account_number: e.target.value }))} data-testid="bank-account-number-input" className="rounded-none" placeholder="0000 0000" />
          </div>
        </div>
        <button type="submit" disabled={savingBank} data-testid="bank-save-button"
          className="bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
          {savingBank ? "Saving…" : "Save Bank Details"}
        </button>
      </form>

      <form onSubmit={saveBiz} className="bg-white border border-[#C5A059]/25 p-8 max-w-lg space-y-5 mt-8" data-testid="business-settings-form">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-1">Invoices</p>
          <h2 className="font-serif text-2xl font-light">Business Details</h2>
          <p className="text-sm text-[#4A4A4A] mt-2">Printed on every invoice you generate.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase">ABN</Label>
          <Input value={biz.abn} onChange={(e) => setBiz((b) => ({ ...b, abn: e.target.value }))} data-testid="biz-abn-input" className="rounded-none" placeholder="00 000 000 000" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase">Business Address</Label>
          <Input value={biz.business_address} onChange={(e) => setBiz((b) => ({ ...b, business_address: e.target.value }))} data-testid="biz-address-input" className="rounded-none" placeholder="Street, Suburb, State, Postcode" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase">Phone</Label>
            <Input value={biz.business_phone} onChange={(e) => setBiz((b) => ({ ...b, business_phone: e.target.value }))} data-testid="biz-phone-input" className="rounded-none" placeholder="0400 000 000" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase">Business Email</Label>
            <Input type="email" value={biz.business_email} onChange={(e) => setBiz((b) => ({ ...b, business_email: e.target.value }))} data-testid="biz-email-input" className="rounded-none" placeholder="hello@…" />
          </div>
        </div>
        <button type="submit" disabled={savingBiz} data-testid="biz-save-button"
          className="bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
          {savingBiz ? "Saving…" : "Save Business Details"}
        </button>
      </form>
    </div>
  );
}
